import React, { useContext, useEffect } from "react";
import { formatDateWithTime } from "Common/HelperClass";
import { useState } from "react";
import { useNavigate } from "react-router";
import TableSkeletonElement from "Components/GMS/Products/components/TableSkeletonElement.js";
import { PivotSortableColumn } from "../constants/CSConstants";
import { CSContext } from "../contexts/CSContext";
import { sortTableField } from "../utils/CSHelper";
import NoDataComponent from "Components/Common/NoDataComponent";
import Table from "Components/DesignSystem/Table/Table";
import { PAGE_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import Radio from "Components/DesignSystem/Radio/Radio";
import { TableColumn, SortDirection } from "types/DesignSystem/Table";

/**
 * The create-mode version list, on the design-system `Table` (2026-09-08).
 *
 * Behavioural gates unchanged: `sortTableField` over a COPY (unlike `CSTable`,
 * nothing indexes the source array here), selection is `selectedIndex` into the
 * SORTED order — including the legacy quirk that a sort change re-points the same
 * index at a different row — and the `[selectedIndex]` effect still pushes
 * `handlePivotData(sortedData[selectedIndex])`.
 *
 * One indicator nuance: the legacy header drew the ATTRIBUTES sort arrow on both
 * "Total Attributes" and "Attributes" (both sort the same `attribute` field); the
 * DS header shows it on "Attributes" only, since indicators key by column id.
 */
function CSVersionTable(props: any) {
    const { pivotVersionList, loading, handlePivotData } = useContext(CSContext)

    const [sortColumn, setSortColumn] = useState<PivotSortableColumn | null>(null);
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

    const navigate = useNavigate();

    const sortedData = sortTableField([...pivotVersionList ?? []], sortColumn, sortDirection)

    const handleSortChange = (columnId: string, direction: SortDirection) => {
        // 'total_attributes' is a distinct column id but sorts the same field the
        // legacy header did (attribute-count has no field of its own).
        const column = columnId === 'total_attributes' ? PivotSortableColumn.ATTRIBUTES : columnId as PivotSortableColumn;
        const next = direction ?? 'asc';
        setSortColumn(column);
        setSortDirection(next === 'desc' ? 'desc' : 'asc');
    };

    useEffect(() => {
        handlePivotData(sortedData[props?.selectedIndex])
    }, [props?.selectedIndex])

    const selectRow = (defaultIndex: number) => {
        props?.setSelectedIndex(defaultIndex)
        handlePivotData(sortedData[defaultIndex])
    };

    const columns: TableColumn<PivotBaseItem>[] = [
        {
            id: 'select',
            header: '',
            accessor: () => null,
            width: '48px',
            render: (_value, row, rowIndex) => (
                <Radio
                    label=""
                    aria-label={`Select version ${row?.version_name ?? ''}`}
                    value={row?.uid ?? rowIndex}
                    name={`radio-group-${rowIndex}`}
                    checked={props?.selectedIndex === rowIndex}
                    onChange={() => selectRow(rowIndex)}
                    data-testid={`csversion-radio-${row?.uid ?? rowIndex}`}
                />
            ),
        },
        {
            id: PivotSortableColumn.CATEGORY,
            header: 'Category',
            accessor: (r) => r?.category_name ?? '',
            sortable: true,
            render: (_value, row) => (
                <div
                    role="button"
                    tabIndex={0}
                    data-testid={`csversion-category-${row?.uid}`}
                    className="tw-text-accent-bold hover:tw-underline tw-text-sm tw-leading-tight tw-cursor-pointer"
                    onClick={() => { navigate(`/gms/commend/model/${row?.category_id}`); }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/gms/commend/model/${row?.category_id}`);
                        }
                    }}
                >
                    {row?.category_name ?? ""}
                </div>
            ),
        },
        {
            id: PivotSortableColumn.VERSION,
            header: 'Identifier',
            accessor: (r) => r?.version_name ?? '',
            sortable: true,
        },
        {
            id: 'total_attributes',
            header: 'Total Attributes',
            accessor: (r) => r?.attribute?.split(",").length ?? 0,
            sortable: true,
        },
        {
            id: PivotSortableColumn.ATTRIBUTES,
            header: 'Attributes',
            accessor: (r) => r?.attribute ?? '',
            sortable: true,
            render: (_value, row) => (
                <div className="tw-text-sm tw-leading-tight tw-overflow-hidden tw-truncate">{row?.attribute}</div>
            ),
        },
        {
            id: PivotSortableColumn.CREATED_ON,
            header: 'Created on',
            accessor: (r) => formatDateWithTime(r?.created_on) || "",
            sortable: true,
            cellType: 'meta',
        },
        {
            id: PivotSortableColumn.LAST_UPDATED_BY,
            header: 'Last updated by',
            accessor: (r) => r?.updated_by?.username ?? '',
            sortable: true,
        },
    ];

    if (loading) { return <div className=""><TableSkeletonElement /></div> }

    if (loading === false && pivotVersionList?.length === 0) { return <div><NoDataComponent /></div> }

    return (
        <div className="tw-mt-3">
            <Table
                /* Bound the grid so <main> stops scrolling the page — see
                   `pageGridHeight.ts` for the measured reserve. `stickyHeader`
                   needs it: sticky resolves against a scrolling ancestor. */
                stickyHeader
                maxHeight={PAGE_GRID_MAX_HEIGHT}
                columns={columns}
                data={sortedData ?? []}
                rowKey={(r) => r?.uid}
                striped
                hoverable
                bordered
                sortState={sortColumn ? { columnId: sortColumn, direction: sortDirection } : undefined}
                onSort={handleSortChange}
                id="cs-version-table"
                data-testid="cs-version-table"
            />
        </div>
    );
}

export default CSVersionTable
